// Text renderer — plan §6.3. <textarea> auto-resize, max 5000 char (options.maxLength).
// Ö8 (Finar UX P2): sınıra %90 yaklaşınca sayaç amber + uyarı, sınırda kırmızı; yerel
// `maxLength` yazmayı durdurur ama yapıştırma kesmesi artık sessiz değil ("N karakter sığmadı").

import { useEffect, useId, useRef, useState } from "react";
import type { QuestionSnapshot } from "@mahir/shared";
import { FORM_FILL_COPY, textCounterState } from "../../../lib/form-fill-copy";

const TONE_COLOR = {
  normal: "var(--color-text-3)",
  near: "var(--color-warning-text)",
  at: "var(--color-error)",
} as const;

interface Props {
  question: QuestionSnapshot;
  value: string;
  onChange: (v: string) => void;
}

export function TextRenderer({ question, value, onChange }: Props): JSX.Element {
  const ref = useRef<HTMLTextAreaElement>(null);
  const maxLength =
    (question.options as { maxLength?: number } | null | undefined)?.maxLength ?? 5000;

  // Composite parçaları üst sorunun kodunu taşır → id kodtan değil useId'den (tekil).
  const counterId = useId();
  const [truncated, setTruncated] = useState(0);
  const counter = textCounterState(value.length, maxLength);

  // Auto-resize
  useEffect(() => {
    const ta = ref.current;
    if (!ta) return;
    ta.style.height = "auto";
    ta.style.height = `${ta.scrollHeight}px`;
  }, [value]);

  return (
    <div>
      <textarea
        ref={ref}
        value={value}
        maxLength={maxLength}
        onChange={(e) => {
          if (truncated > 0 && e.target.value.length < maxLength) setTruncated(0);
          onChange(e.target.value);
        }}
        onPaste={(e) => {
          const ta = e.currentTarget;
          const pasted = e.clipboardData.getData("text").length;
          const selected = ta.selectionEnd - ta.selectionStart;
          const room = maxLength - (value.length - selected);
          setTruncated(pasted > room ? pasted - Math.max(room, 0) : 0);
        }}
        aria-describedby={counterId}
        rows={3}
        className="w-full rounded-lg px-3 py-2 text-sm resize-none"
        style={{
          background: "var(--color-surface)",
          border: "1px solid var(--color-border)",
          color: "var(--color-text)",
          fontFamily: "var(--font-sans)",
          minHeight: 72,
        }}
      />
      <div
        id={counterId}
        className="mt-1 text-xs flex justify-between gap-3"
        style={{ fontFamily: "var(--font-mono)" }}
      >
        <span style={{ color: TONE_COLOR[counter.tone], fontFamily: "var(--font-sans)" }}>
          {truncated > 0 ? FORM_FILL_COPY.textCounter.truncated(truncated) : counter.message}
        </span>
        {/* Duyuru yalnız eşik değişince (sabit metin) — sayaç her tuşta okunmaz. */}
        <span className="sr-only" aria-live="polite">
          {truncated > 0 ? FORM_FILL_COPY.textCounter.truncated(truncated) : counter.announce}
        </span>
        <span style={{ color: TONE_COLOR[counter.tone], whiteSpace: "nowrap" }}>
          {FORM_FILL_COPY.textCounter.count(value.length, maxLength)}
        </span>
      </div>
    </div>
  );
}
